import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthContext/AuthContext";
import CustomBottomNavbar from "../../Users/Post Module/FormsBottomNavbar";
import { BASE_URL } from "../../Api/ApiUrls";
import DeleteFailureAlert from '../../../Components/Users/Alertpage/DeleteAlert/DeleteFailureAlert';
import DeleteSuccessAlert from '../../../Components/Users/Alertpage/DeleteAlert/DeleteSuccessAlert';
import DeleteConfirmAlert from '../../../Components/Users/Alertpage/DeleteAlert/DeleteConfirmAlert';


const BuiltSuitProperties = () => {
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
const [showDeleteFailure, setShowDeleteFailure] = useState(false);
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [requestToDelete, setRequestToDelete] = useState(null);
const [alertMessage, setAlertMessage] = useState("");
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const colors = {
    primary: "#1a1a1a",
    secondary: "#8B7355",
    accent: "#A52A2A",
    background: "#b9a990",
    cardBackground: "#FFFFFF",
    textDark: "#1a1a1a",
    textLight: "#FFFFFF",
    textMuted: "#666666"
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

// Format budget function for min and max
const formatBudget = (min, max) => {
  if (!min && !max) return "Not specified";
  
  const formatNumber = (num) => {
    const numValue = parseFloat(num) || 0;
    
    if (numValue >= 10000000) {       // 1 Crore+
      return `₹${(numValue / 10000000).toFixed(1)} Cr`;
    } else if (numValue >= 100000) {  // 1 Lakh+
      return `₹${(numValue / 100000).toFixed(1)} L`;
    } else if (numValue >= 1000) {    // 1 Thousand+
      return `₹${(numValue / 1000).toFixed(1)} K`;
    } else {
      return `₹${numValue}`;
    }
  };
  
  const minNum = parseFloat(min) || 0;
  const maxNum = parseFloat(max) || 0;
  
  if (minNum === maxNum) return formatNumber(minNum);
  
  const minFormatted = formatNumber(minNum);
  const maxFormatted = formatNumber(maxNum);
  
  return `${minFormatted} - ${maxFormatted}`;
};
 const handleDeleteProperty = (e, property) => {
  e.stopPropagation();
  setPropertyToDelete(property);
  setShowDeleteConfirm(true);
};
const performDelete = async () => {
  if (!propertyToDelete) return;
  
  setDeletingId(propertyToDelete.property_id);
  
  try {
    await axios.delete(`${BASE_URL}/property/${propertyToDelete.property_id}/`);
    
    // Remove the deleted property from state
    setData(prevData => prevData.filter(item => item.property_id !== propertyToDelete.property_id));
    
    setAlertMessage("JV/JD & Build to Suit property deleted successfully!");
    setShowDeleteSuccess(true);
    
  } catch (err) {
    console.error("Error deleting property:", err);
    setAlertMessage("Failed to delete property. Please try again.");
    setShowDeleteFailure(true);
  } finally {
    setDeletingId(null);
    setPropertyToDelete(null);
  }
};

  // Handle card click to navigate to detail page
  const handleCardClick = (propertyId) => {
    navigate(`/jv-jv-property-detail/${propertyId}`);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both jv/jd and build to suit in parallel
        const [jvjdRes, buildToSuitRes] = await Promise.all([
          axios.post(`${BASE_URL}/properties/filter/`, {
            include: { user_id: Number(userId), type: "jv/jd" },
            exclude: {}
          }),
          axios.post(`${BASE_URL}/properties/filter/`, {
            include: { user_id: Number(userId), type: "build to suit" },
            exclude: {}
          })
        ]);

        const jvjdData = jvjdRes.data?.data || [];
        const buildToSuitData = buildToSuitRes.data?.data || [];

        // Merge both arrays and sort newest first
        const combined = [...jvjdData, ...buildToSuitData].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setData(combined);
        setLoading(false);
      } catch (err) {
        setError("Failed to load JV/JD & Build to Suit properties");
        setLoading(false);
      }
    };

    if (userId) {
      fetchData();
    } else {
      setLoading(false);
      setError("User not authenticated");
    }
  }, [userId]);
  
  // Render property detail function
  const renderPropertyDetail = (label, value, color = colors.textDark) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
      <Typography variant="body2" color={color} sx={{ 
        fontSize: '0.75rem', 
        lineHeight: 1.2, 
        minWidth: 120, 
        flexShrink: 0,
        fontWeight: 'bold'
      }}>
        {label}:
      </Typography>
      <Typography variant="body2" color={colors.textDark} sx={{ 
        fontSize: '0.75rem', 
        lineHeight: 1.2 
      }}>
        {value || "Not specified"}
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: 'rgb(239, 231, 221)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <CircularProgress size={40} thickness={5} sx={{ color: '#000000' }} />
        <Typography variant="h7" sx={{ color: '#000000', fontWeight: 600 }}>
          Loading JV/JD & Build to Suit properties...
        </Typography>
      </Box>
    );
  }

  if (error) return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      minHeight: "100vh", 
      backgroundColor: colors.background, 
      justifyContent: "center", 
      alignItems: "center" 
    }}>
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ 
  display: 'flex', 
  flexDirection: 'column', 
  minHeight: '100vh', 
  backgroundColor: colors.background 
}}>
<AppBar
  position="fixed"
  sx={{
    backgroundColor: colors.primary,
    color: colors.textLight,
    zIndex: 1200,
  }}
>
  <Toolbar>
    <IconButton
      onClick={() => navigate(-1)}
      sx={{ color: colors.textLight }}
    >
      <ArrowBackIosNewIcon />
    </IconButton>

    <Typography variant="h6" sx={{ ml: 2 }}>
      JV/JD & Build to Suit Properties
    </Typography>
  </Toolbar>
</AppBar>

  {/* Main Content Area - Added pb to create space for bottom navbar */}
  <Box sx={{ 
    p: 1.5, 
    flex: 1, 
        pt: { xs: '80px', sm: '100px' },  
    pb: 10, // Increased from pb: 8 to pb: 10
    backgroundColor: colors.background,
    overflowY: 'auto'
  }}>
    {data.length === 0 ? (
      <Typography variant="h6" textAlign="center" sx={{ mt: 4, color: colors.textDark }}>
        No JV/JD or Build to Suit properties found
      </Typography>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {data.map((item) => (
          <Card 
            key={item.property_id} 
            onClick={() => handleCardClick(item.property_id)}
            sx={{ 
              borderRadius: 2, 
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)', 
              border: `1px solid #e0e0e0`, 
              overflow: 'hidden',
              cursor: 'pointer',
              mb: 1, // Added margin bottom for better spacing
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {/* Header section with Type */}
              <Box sx={{ 
                p: 1, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                backgroundColor: colors.primary 
              }}>
                <Box sx={{ 
                  backgroundColor: "#b9a990", 
                  color: colors.primary,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '0.9rem', lineHeight: 1 }}>
                    {item.type?.toUpperCase() || "JV/JD"}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  backgroundColor: colors.cardBackground, 
                  color: colors.primary,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1, fontSize: '0.9rem' }}>
                    Budget: {formatBudget(item.min_budget, item.max_budget)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 1.5 }}>
                <Box sx={{ display: 'grid', gap: 0.75 }}>
                  {/* Property Type */}
                  {renderPropertyDetail("Property Type", item.property_type)}
                  
                  {/* Location */}
                  {renderPropertyDetail("Location", item.location)}
                  
                  {/* Combined Min-Max Acres */}
                  {(item.min_acres > 0 || item.max_acres > 0) && renderPropertyDetail(
                    "Min-Max Acres", 
                    item.min_acres > 0 && item.max_acres > 0 
                      ? `${item.min_acres} - ${item.max_acres} acres`
                      : item.min_acres > 0 
                        ? `${item.min_acres} acres`
                        : `${item.max_acres} acres`,
                    colors.textDark
                  )}
                  
                  {/* Facing */}
                  {item.facing && renderPropertyDetail("Facing", item.facing)}
                  
                  {/* Ratio */}
                  {item.ratio && renderPropertyDetail("Ratio", item.ratio)}
                  
                  {/* Road Width */}
                  {item.roadwidth && renderPropertyDetail("Road Width", `${item.roadwidth} ft`)}
                  
                  {/* User and Date with Delete Button */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    mt: 1.5 // Increased margin top
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ 
                        width: 16, 
                        height: 16,  
                        fontSize: '0.5rem',
                        mr: 0.5
                      }}>
                        👤
                      </Avatar>
                      <Typography variant="body2" color={colors.textDark} sx={{ 
                        fontSize: '0.75rem', 
                        lineHeight: 1.2,
                        fontWeight: 'bold'
                      }}>
                        CUST {userId}
                      </Typography>
                      <Typography variant="body2" color={colors.textMuted} sx={{ 
                        fontSize: '0.75rem', 
                        lineHeight: 1.2,
                        ml: 1
                      }}>
                        {formatDate(item.created_at)}
                      </Typography>
                    </Box>
                    
                    {/* Delete Button - Removed position: 'relative' */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {/* Delete Button */}
<Box sx={{ 
  display: 'flex', 
  alignItems: 'center',
  justifyContent: 'center'
}}>
  <IconButton
    onClick={(e) => handleDeleteProperty(e, item)}
    disabled={deletingId === item.property_id}
    sx={{
      width: 28,
      height: 28,
      "&:hover": {
        backgroundColor: "#ffebee",
      }
    }}
  >
    {deletingId === item.property_id ? (
      <CircularProgress size={18} sx={{ color: "#ff4444" }} />
    ) : (
      <DeleteIcon sx={{ color: "#ff4444", fontSize: 18 }} />
    )}
  </IconButton>
</Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    )}
  </Box>


  {/* Delete Success */}
      {showDeleteSuccess && (
        <DeleteSuccessAlert
          message={alertMessage}
          onClose={() => {
            setShowDeleteSuccess(false);
            setAlertMessage("");
          }}
        />
      )}
      
      {/* Delete Failure */}
      {showDeleteFailure && (
        <DeleteFailureAlert
          message={alertMessage}
          onClose={() => {
            setShowDeleteFailure(false);
            setAlertMessage("");
          }}
        />
      )}
{showDeleteConfirm && propertyToDelete && (
  <DeleteConfirmAlert
    message={`Are you sure you want to delete this ${propertyToDelete.type || 'JV/JD'} property? This action cannot be undone.`}
    onOk={() => {
      setShowDeleteConfirm(false);
      performDelete();
    }}
    onCancel={() => {
      setShowDeleteConfirm(false);
      setPropertyToDelete(null);
    }}
  />
)}
  {/* Bottom Navbar - Fixed position */}
  <Box sx={{ 
    position: "fixed", 
    bottom: 0, 
    left: 0, 
    right: 0, 
    zIndex: 1000 
  }}>
    <CustomBottomNavbar />
  </Box>
</Box>
  );
};

export default BuiltSuitProperties;